import React from 'react';
import { compose } from 'redux';
import PropTypes from 'prop-types';
// Material UI
import { makeStyles } from '@material-ui/core/styles';
import MuiButton from '@material-ui/core/Button';
import MuiIconButton from '@material-ui/core/IconButton';
import MuiFab from '@material-ui/core/Fab';
import MuiCircularProgress from '@material-ui/core/CircularProgress';
// Custom
import withTooltip from 'hocs/WithTooltip';
import AppIcon from 'components/icons/AppIcon';
// Internal
import withLocalTheme from './hocs/WithLocalTheme';
import { getInternalLink, getMuiColor } from './internal/Tools';

// Styles
const useStyles = makeStyles(() => ({
  wrapper: {
    position: 'relative',
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  iconProgress: {
    position: 'absolute',
    top: 4,
    left: 4,
    zIndex: 1,
  },
}));

// ============================================================================ //
// -- Base button -- //
const BaseButton = compose(withTooltip, withLocalTheme)(
  ({
    type,
    label,
    labelPosition,
    iconId,
    customIcon,
    backgroundColor,
    color,
    variant,
    loading,
    disabled,
    maxWidth,
    path,
    href,
    onClick,
    className,
    ...otherProps
  }) => {
    // Styles
    const classes = useStyles();

    // Components props
    const buttonProps = {
      type,
      color,
      variant,
      disabled: disabled || loading,
      href,
      onClick,
      ...otherProps,
    };

    // Icon
    let icon = customIcon;
    if (!icon && iconId) {
      icon = <AppIcon id={iconId} />;
    }

    return (
      <div className={`${classes.wrapper} ${className}`}>
        <MuiButton
          {...buttonProps}
          className={classes.root}
          component={path ? getInternalLink(path) : 'button'}
          style={{
            outline: 'none', background: backgroundColor, minWidth: '40px', ...(maxWidth && { maxWidth: `${maxWidth}px` }),
          }}
          startIcon={labelPosition === 'after' ? icon : null}
          endIcon={labelPosition === 'before' ? icon : null}
        >
          <div className="text-truncate">
            {label}
          </div>
        </MuiButton>
        {loading && (
          <MuiCircularProgress color="primary" size={24} className={classes.buttonProgress} />
        )}
      </div>
    );
  },
);

BaseButton.displayName = 'BaseButton';

BaseButton.propTypes = {
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  labelPosition: PropTypes.oneOf(['before', 'after']),
  iconId: PropTypes.string,
  customIcon: PropTypes.element,
  backgroundColor: PropTypes.string,
  color: PropTypes.oneOf(['default', 'inherit', 'primary', 'secondary']).isRequired,
  variant: PropTypes.oneOf(['text', 'outlined', 'contained', 'fab']).isRequired,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  maxWidth: PropTypes.number,
  path: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      path: PropTypes.string.isRequired,
      targetBlank: PropTypes.bool.isRequired,
    }),
  ]),
  href: PropTypes.string,
  className: PropTypes.string,
  onClick: PropTypes.func,
};

BaseButton.defaultProps = {
  type: 'button',
  backgroundColor: '',
  label: null,
  labelPosition: 'after',
  iconId: null,
  customIcon: null,
  loading: false,
  disabled: false,
  maxWidth: null,
  path: null,
  onClick: null,
  className: '',
};

// -- Base theme button -- //
const BaseTextButton = ({
  primary,
  secondary,
  inherit,
  ...otherProps
}) => (
  <BaseButton
    color={getMuiColor(primary, secondary, inherit)}
    {...otherProps}
  />
);

BaseTextButton.propTypes = {
  primary: PropTypes.bool,
  secondary: PropTypes.bool,
  inherit: PropTypes.bool,
};

BaseTextButton.defaultProps = {
  primary: true,
  secondary: false,
  inherit: false,
};

// -- Exported specific buttons -- //
export const TextButton = React.memo((props) => (<BaseTextButton variant="text" {...props} />));
TextButton.displayName = 'TextButton';
export const ContainedButton = React.memo((props) => (<BaseTextButton variant="contained" {...props} />));
ContainedButton.displayName = 'ContainedButton';
export const OutlinedButton = React.memo((props) => (<BaseTextButton variant="outlined" {...props} />));
OutlinedButton.displayName = 'OutlinedButton';

// ============================================================================ //
// -- Base icon button -- //
const BaseIconButton = compose(withTooltip, withLocalTheme)(
  ({
    iconId,
    color,
    loading,
    disabled,
    path,
    inheritFontSize,
    onClick,
    ...otherProps
  }) => {
    // Styles
    const classes = useStyles();

    // Components props
    const buttonProps = {
      color, disabled: disabled || loading, onClick, ...otherProps,
    };

    // Icon style
    let iconStyle = {};
    if (inheritFontSize) {
      iconStyle = {
        ...iconStyle,
        fontSize: 'inherit',
      };
    }

    return (
      <div className={classes.wrapper}>
        <MuiIconButton
          {...buttonProps}
          component={path ? getInternalLink(path) : 'button'}
          style={{ outline: 'none' }}
        >
          <AppIcon id={iconId} style={iconStyle} />
        </MuiIconButton>
        {loading && (
          <MuiCircularProgress color="primary" size={40} className={classes.iconProgress} />
        )}
      </div>
    );
  },
);

BaseIconButton.propTypes = {
  iconId: PropTypes.string.isRequired,
  color: PropTypes.oneOf(['default', 'inherit', 'primary', 'secondary']).isRequired,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  path: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      path: PropTypes.string.isRequired,
      targetBlank: PropTypes.bool.isRequired,
    }),
  ]),
  inheritFontSize: PropTypes.bool,
  onClick: PropTypes.func,
};

BaseIconButton.defaultProps = {
  loading: false,
  disabled: false,
  path: null,
  inheritFontSize: false,
  onClick: null,
};

// -- Exported specific buttons -- //
export const IconButton = React.memo(({
  primary, secondary, inherit, ...otherProps
}) => (
  <BaseIconButton
    color={getMuiColor(primary, secondary, inherit)}
    {...otherProps}
  />
));

IconButton.displayName = 'IconButton';

IconButton.propTypes = {
  primary: PropTypes.bool,
  secondary: PropTypes.bool,
  inherit: PropTypes.bool,
};

IconButton.defaultProps = {
  primary: false,
  secondary: false,
  inherit: false,
};

// ============================================================================ //
// -- Base FAB button -- //
const BaseFaButton = compose(withTooltip, withLocalTheme)(
  ({
    iconId, color, loading, disabled, path, onClick, ...otherProps
  }) => {
    // Styles
    const classes = useStyles();

    // Components props
    const buttonProps = {
      color, disabled: disabled || loading, onClick, ...otherProps,
    };

    return (
      <div className={classes.wrapper}>
        <MuiFab
          {...buttonProps}
          component={path ? getInternalLink(path) : 'button'}
        >
          <AppIcon id={iconId} />
        </MuiFab>
        {loading && (
          <MuiCircularProgress color="primary" size={40} className={classes.iconProgress} />
        )}
      </div>
    );
  },
);

BaseFaButton.propTypes = {
  iconId: PropTypes.string.isRequired,
  color: PropTypes.oneOf(['default', 'inherit', 'primary', 'secondary']).isRequired,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  path: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      path: PropTypes.string.isRequired,
      targetBlank: PropTypes.bool.isRequired,
    }),
  ]),
  onClick: PropTypes.func,
};

BaseFaButton.defaultProps = {
  loading: false,
  disabled: false,
  path: null,
  onClick: null,
};

// -- Exported specific buttons -- //
export const FaButton = React.memo(({
  primary, secondary, inherit, ...otherProps
}) => (
  <BaseFaButton
    color={getMuiColor(primary, secondary, inherit)}
    {...otherProps}
  />
));

FaButton.displayName = 'FaButton';

FaButton.propTypes = {
  primary: PropTypes.bool,
  secondary: PropTypes.bool,
  inherit: PropTypes.bool,
};

FaButton.defaultProps = {
  primary: false,
  secondary: false,
  inherit: false,
};



// WEBPACK FOOTER //
// ./components/core/Button.js